import React, { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import {
    FormGroup,
    Label,
    Input,
    Button,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    CardTitle,
    Row,
    Col,
    Table,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from "reactstrap";
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import moment from 'moment'
import OrderService from '../../services/order.service'
import './CSS.css'

function OrderIDDetails() {
    const { id } = useParams();
    const [orderData, setOrderData] = useState(null);
    const [toolsData, setToolsData] = useState(null);
    const [botActionData, setBotActionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const abortControllerRef = useRef(null);

    useEffect(() => {
        fetchOrderDetails();

        return () => {
            // Hủy request nếu component unmount
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownRef]);

    const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

    const fetchOrderDetails = async () => {
        try {
            // Hủy request cũ nếu có
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            // Tạo AbortController mới
            abortControllerRef.current = new AbortController();
            const signal = abortControllerRef.current.signal;

            setLoading(true);
            const response = await OrderService.getOrderById(id, signal);
            setOrderData(response.order);
            setToolsData(response.order.tools || []);
            setBotActionData(response.order.botAction || []);
            setLoading(false);
        } catch (err) {
            // Không hiển thị lỗi nếu request bị hủy
            if (err.name === 'AbortError') {
                console.log('Request aborted');
                return;
            }

            setError('Không thể tải thông tin đơn hàng');
            setLoading(false);
            Swal.fire({
                icon: 'error',
                title: 'Lỗi',
                text: 'Không thể tải thông tin đơn hàng',
            });
        }
    };

    const handleUpdateStatus = async (newStatus) => {
        if (newStatus === orderData?.paymentStatus) return;

        try {
            // Nếu chuyển sang trạng thái "Đã thanh toán", hiển thị hộp thoại xác nhận
            if (newStatus === "Đã thanh toán") {
                const confirmResult = await Swal.fire({
                    title: 'Xác nhận thanh toán',
                    text: 'Bạn có muốn tạo tài khoản và gửi email cho khách hàng không?',
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonColor: '#6bd098',
                    cancelButtonColor: '#ef8157',
                    confirmButtonText: 'Có, tạo và gửi email',
                    cancelButtonText: 'Không',
                    allowOutsideClick: false,
                    customClass: {
                        popup: 'swal-custom-popup'
                    },
                    didOpen: () => {
                        // Thêm style cho borderRadius
                        const popup = Swal.getPopup();
                        popup.style.borderRadius = '30px';
                        popup.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.2)';
                    }
                });

                // Nếu người dùng không xác nhận, thoát khỏi hàm
                if (!confirmResult.isConfirmed) {
                    return;
                }
            }

            setUpdatingStatus(true);

            // Hiển thị Swal với spinner - lưu tham chiếu để cập nhật sau
            const loadingSwal = Swal.fire({
                title: 'Đang xử lý',
                text: 'Đang cập nhật trạng thái đơn hàng...',
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                customClass: {
                    popup: 'swal-custom-popup'
                },
                didOpen: () => {
                    Swal.showLoading();

                    // Thêm style cho borderRadius
                    const popup = Swal.getPopup();
                    popup.style.borderRadius = '30px';
                    popup.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.2)';
                }
            });

            const response = await OrderService.updateOrder(id, newStatus);

            if (response.success) {
                // Fetch lại dữ liệu đơn hàng từ server thay vì chỉ cập nhật trạng thái cục bộ
                await fetchOrderDetails();

                // Cập nhật Swal hiện tại thay vì tạo mới
                Swal.update({
                    icon: 'success',
                    title: 'Cập nhật thành công',
                    text: response.message || 'Trạng thái đơn hàng đã được cập nhật',
                    showConfirmButton: true,
                    confirmButtonText: 'Đóng',
                    timer: 1500,
                    allowOutsideClick: true
                });

                // Đảm bảo spinner sẽ bị ẩn
                Swal.hideLoading();
            } else {
                // Cập nhật Swal hiện tại thay vì tạo mới
                Swal.update({
                    icon: 'error',
                    title: 'Lỗi',
                    text: response.message || 'Không thể cập nhật trạng thái đơn hàng',
                    showConfirmButton: true,
                    confirmButtonText: 'Đóng',
                    allowOutsideClick: true
                });

                // Đảm bảo spinner sẽ bị ẩn
                Swal.hideLoading();
            }
        } catch (err) {
            // Cập nhật Swal hiện tại thay vì tạo mới
            Swal.update({
                icon: 'error',
                title: 'Lỗi',
                text: err.message || 'Không thể cập nhật trạng thái đơn hàng',
                showConfirmButton: true,
                confirmButtonText: 'Đóng',
                allowOutsideClick: true
            });

            // Đảm bảo spinner sẽ bị ẩn
            Swal.hideLoading();
        } finally {
            setUpdatingStatus(false);
            setDropdownOpen(false);
        }
    };

    function back() {
        // Hủy request nếu đang loading và người dùng click nút quay lại
        if (loading && abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        window.history.back();
    }

    function getStatusColor(status) {
        if (status === "Đã thanh toán") return "#6bd098";
        if (status === "Chưa thanh toán") return "#fbc658";
        if (status === "Đã hủy") return "#ef8157";
        return "#ef8157";
    }

    function getActionStatusColor(status) {
        // Kiểm tra nếu trạng thái thành công hoặc các từ tích cực
        if (status && (
            status.toLowerCase().includes('thành công') ||
            status.toLowerCase().includes('hoàn thành') ||
            status.toLowerCase().includes('success') ||
            status.toLowerCase().includes('completed')
        )) {
            return "#6bd098"; // success color
        }
        // Kiểm tra nếu trạng thái thất bại hoặc các từ tiêu cực
        else if (status && (
            status.toLowerCase().includes('thất bại') ||
            status.toLowerCase().includes('lỗi') ||
            status.toLowerCase().includes('hủy') ||
            status.toLowerCase().includes('failed') ||
            status.toLowerCase().includes('error')
        )) {
            return "#ef8157"; // danger color
        }
        return "inherit"; // default color
    }

    function getActionStatusBgColor(status) {
        // Màu nền cho trạng thái thành công
        if (status && (
            status.toLowerCase().includes('thành công') ||
            status.toLowerCase().includes('hoàn thành') ||
            status.toLowerCase().includes('success') ||
            status.toLowerCase().includes('completed')
        )) {
            return "rgba(107, 208, 152, 0.15)"; // success background with opacity
        }
        // Màu nền cho trạng thái thất bại
        else if (status && (
            status.toLowerCase().includes('thất bại') ||
            status.toLowerCase().includes('lỗi') ||
            status.toLowerCase().includes('hủy') ||
            status.toLowerCase().includes('failed') ||
            status.toLowerCase().includes('error')
        )) {
            return "rgba(239, 129, 87, 0.15)"; // danger background with opacity
        }
        return "transparent"; // default background
    }

    function getStatusOptions() {
        if (orderData?.paymentStatus === "Đã thanh toán") {
            return [
                { value: "Đã thanh toán", label: "Đã thanh toán", color: "#6bd098" }
            ];
        } else if (orderData?.paymentStatus === "Đã hủy") {
            return [
                { value: "Đã hủy", label: "Đã hủy", color: "#ef8157" }
            ];
        } else {
            return [
                { value: "Chưa thanh toán", label: "Chưa thanh toán", color: "#fbc658" },
                { value: "Đã thanh toán", label: "Đã thanh toán", color: "#6bd098" },
                { value: "Đã hủy", label: "Đã hủy", color: "#ef8157" }
            ];
        }
    }

    // Hiển thị nút quay lại trong mọi trường hợp
    const renderBackButton = () => (
        <Button
            style={{ marginBottom: '30px', borderRadius: '30px' }}
            color="info"
            onClick={back}>
            &laquo; Quay lại
        </Button>
    );

    // Hiển thị spinner trung tâm trang khi đang loading
    const renderLoading = () => (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 200px)' }}>
            <div className="text-center">
                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="sr-only">Đang tải...</span>
                </div>
            </div>
        </div>
    );

    if (error) {
        return (
            <div className="content">
                {renderBackButton()}
                <Card>
                    <CardBody className="text-center">
                        <h4 className="text-danger">{error}</h4>
                        <Button color="primary" onClick={fetchOrderDetails}>Thử lại</Button>
                    </CardBody>
                </Card>
            </div>
        );
    }

    return (
        <div className="content">
            {renderBackButton()}
            
            {loading ? (
                renderLoading()
            ) : (
                <>
                    <Row>
                        <Col lg="4" md="4" sm="4">
                            <Card style={{ padding: '15px', borderRadius: '30px' }}>
                                <CardHeader>
                                    <Label style={{ fontSize: '24px', color: 'DimGray', fontWeight: 'bold' }}>Khách hàng</Label>
                                </CardHeader>

                                <CardBody>
                                    <FormGroup>
                                        <div className="form-row">
                                            <div className="form-group col-md-12">
                                                <Label for="email">Email</Label>
                                                <Input
                                                    value={orderData?.email || ''}
                                                    id="email"
                                                />
                                            </div>
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group col-md-12">
                                                <Label for="fullname">Họ và tên</Label>
                                                <Input
                                                    value={orderData?.fullName || ''}
                                                    id="fullname"
                                                />
                                            </div>
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group col-md-12">
                                                <Label for="phone">Số điện thoại</Label>
                                                <Input
                                                    value={orderData?.phone || ''}
                                                    id="phone"
                                                />
                                            </div>
                                        </div>
                                    </FormGroup>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col lg="8" md="8" sm="8">
                            <Card style={{ padding: '15px', borderRadius: '30px' }}>
                                <CardHeader>
                                    <Label style={{ fontSize: '24px', color: 'DimGray', fontWeight: 'bold' }}>Đơn hàng #{orderData?.id}</Label>
                                    <br />
                                    <Label className="text-success" style={{ fontSize: '16px', fontWeight: 'normal' }}>{orderData ? moment(orderData.createdAt).format('HH:mm:ss DD/MM/YYYY') : ''}</Label>
                                </CardHeader>

                                <CardBody>
                                    <FormGroup>
                                        <div className="form-row">
                                            <div className="form-group col-md-6">
                                                <Label for="status">Trạng thái</Label>
                                                <div
                                                    ref={dropdownRef}
                                                    style={{
                                                        position: "relative",
                                                        width: "100%"
                                                    }}
                                                >
                                                    <button
                                                        onClick={toggleDropdown}
                                                        disabled={updatingStatus}
                                                        style={{
                                                            backgroundColor: getStatusColor(orderData?.paymentStatus),
                                                            color: "white",
                                                            fontWeight: "bold",
                                                            textAlign: "center",
                                                            border: "none",
                                                            outline: "none",
                                                            boxShadow: "none",
                                                            borderRadius: "5px",
                                                            width: "100%",
                                                            padding: "10px 15px",
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                            transition: "filter 0.2s ease"
                                                        }}
                                                        className="status-dropdown-button"
                                                        onMouseOver={(e) => e.currentTarget.style.filter = "brightness(90%)"}
                                                        onMouseOut={(e) => e.currentTarget.style.filter = "brightness(100%)"}
                                                    >
                                                        <span>{orderData?.paymentStatus}</span>
                                                        <i className="fa fa-caret-down"></i>
                                                    </button>

                                                    {dropdownOpen && (
                                                        <div
                                                            style={{
                                                                position: "absolute",
                                                                top: "calc(100% + 5px)",
                                                                left: 0,
                                                                right: 0,
                                                                backgroundColor: "#fff",
                                                                boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                                                                zIndex: 1000,
                                                                padding: 0,
                                                                overflow: "hidden",
                                                                borderRadius: "5px",
                                                                width: "100%"
                                                            }}
                                                        >
                                                            {getStatusOptions().map((option, index) => (
                                                                <div
                                                                    key={index}
                                                                    onClick={() => {
                                                                        handleUpdateStatus(option.value);
                                                                        setDropdownOpen(false);
                                                                    }}
                                                                    style={{
                                                                        backgroundColor: option.color,
                                                                        color: "white",
                                                                        fontWeight: "bold",
                                                                        textAlign: "center",
                                                                        padding: "12px 15px",
                                                                        cursor: "pointer",
                                                                        borderBottom: index < getStatusOptions().length - 1 ? "1px solid rgba(255,255,255,0.1)" : "none",
                                                                        transition: "filter 0.2s"
                                                                    }}
                                                                    className="custom-dropdown-item"
                                                                    onMouseOver={(e) => e.currentTarget.style.filter = "brightness(85%)"}
                                                                    onMouseOut={(e) => e.currentTarget.style.filter = "brightness(100%)"}
                                                                >
                                                                    {option.label}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="form-group col-md-6">
                                                <Label for="created">Xử lý bởi</Label>
                                                <Input
                                                    value={orderData?.handleBy || ''}
                                                    id="created"
                                                />
                                            </div>
                                        </div>

                                        <div className="form-row">

                                            <div className="form-group col-md-6">
                                                <Label for="method">Phương thức thanh toán</Label>
                                                <Input
                                                    value={orderData?.paymentMethod || ''}
                                                    id="method"
                                                />
                                            </div>

                                            <div className="form-group col-md-6">
                                                <Label for="price">Tổng tiền</Label>
                                                <Input
                                                    value={orderData ? new Intl.NumberFormat('vi-VN').format(orderData.totalPrice) + ' đ' : ''}
                                                    id="price"
                                                />
                                            </div>
                                        </div>
                                    </FormGroup>

                                    <Row className="mt-4">
                                        <Col md="12">
                                            <div className="d-none d-md-block">
                                                <Table className="table-hover" style={{ lineHeight: "15px", width: "100%" }}>
                                                    <thead className="text-primary">
                                                        <tr>
                                                            <th>Phần mềm</th>
                                                            <th>Hành động</th>
                                                            <th>Email</th>
                                                            <th>Tên người dùng</th>
                                                            <th className="text-right">Giá</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {toolsData && toolsData.length > 0 ? (
                                                            toolsData.map((tool, index) => (
                                                                <tr key={index}>
                                                                    <td>
                                                                        <strong style={{ fontSize: '16px', color: '#6bd098' }}>{tool.toolName}</strong>
                                                                        <br />
                                                                        <span style={{ color: '#6bd098', display: 'block', marginTop: '5px' }}>{tool.attributeString}</span>
                                                                    </td>
                                                                    <td>{tool.actionString}</td>
                                                                    <td>{tool.email}</td>
                                                                    <td>{tool.username}</td>
                                                                    <td className="text-right">{new Intl.NumberFormat('vi-VN').format(tool.price)} đ</td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan="6" className="text-center">Không có dữ liệu phần mềm</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </Table>
                                            </div>

                                            {/* Hiển thị cho thiết bị di động */}
                                            <div className="d-md-none">
                                                {toolsData && toolsData.length > 0 ? (
                                                    toolsData.map((tool, index) => (
                                                        <Card key={index} className="mb-3" style={{ borderRadius: '15px', boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0)', border: '1px solid #51cbce' }}>
                                                            <CardHeader style={{ backgroundColor: '#f9f9f9', padding: '10px 15px', borderTopLeftRadius: '15px', borderTopRightRadius: '15px', borderBottom: '1px solid #51cbce' }}>
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <strong style={{ fontSize: '16px', color: '#51cbce' }}>{tool.toolName}</strong>
                                                                    <span className="text-bold">
                                                                        {new Intl.NumberFormat('vi-VN').format(tool.price)} đ
                                                                    </span>
                                                                </div>
                                                                <div style={{ color: '#51cbce', marginTop: '5px', fontSize: '13px' }}>
                                                                    {tool.attributeString}
                                                                </div>
                                                            </CardHeader>
                                                            <CardBody style={{ padding: '10px 15px' }}>
                                                                <div className="d-flex justify-content-between mb-2">
                                                                    <span style={{ color: '#888' }}>Hành động:</span>
                                                                    <span>{tool.actionString}</span>
                                                                </div>
                                                                <div className="d-flex justify-content-between mb-2">
                                                                    <span style={{ color: '#888' }}>Số lượng:</span>
                                                                    <span>{tool.quantity || 1}</span>
                                                                </div>
                                                                {tool.version && (
                                                                    <div className="d-flex justify-content-between mb-2">
                                                                        <span style={{ color: '#888' }}>Phiên bản:</span>
                                                                        <span>{tool.version}</span>
                                                                    </div>
                                                                )}
                                                                {tool.expired && (
                                                                    <div className="d-flex justify-content-between mb-2">
                                                                        <span style={{ color: '#888' }}>Thời hạn:</span>
                                                                        <span>{tool.expired}</span>
                                                                    </div>
                                                                )}
                                                            </CardBody>
                                                        </Card>
                                                    ))
                                                ) : (
                                                    <div className="text-center p-3">Không có dữ liệu phần mềm</div>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {
                        botActionData && botActionData.length > 0 && (
                            <Row className="mt-4">
                                <Col md="12">
                                    <Card style={{ padding: '15px', borderRadius: '30px' }}>
                                        <CardHeader>
                                            <Label style={{ fontSize: '24px', color: 'DimGray', fontWeight: 'bold' }}>Thao tác</Label>
                                        </CardHeader>

                                        <CardBody>
                                            <Table className="table-hover d-none d-md-table" style={{ lineHeight: "15px", width: "100%" }}>
                                                <thead className="text-primary">
                                                    <tr>
                                                        <th>Thời gian</th>
                                                        <th>Phần mềm</th>
                                                        <th>Người thực hiện</th>
                                                        <th>Hành động</th>
                                                        <th>Trạng thái</th>
                                                        <th>Mô tả</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {botActionData.map((action, index) => (
                                                        <tr
                                                            key={index}
                                                            style={{
                                                                backgroundColor: getActionStatusBgColor(action.status),
                                                            }}
                                                        >
                                                            <td>{moment(action.createdAt).format('HH:mm:ss DD/MM/YYYY')}</td>
                                                            <td>{action.toolName}</td>
                                                            <td>{action.fullName}</td>
                                                            <td>{action.action}</td>
                                                            <td style={{
                                                                color: getActionStatusColor(action.status),
                                                                fontWeight: 'bold'
                                                            }}>{action.status}</td>
                                                            <td>
                                                                {action.message}
                                                                {action.error && (
                                                                    <div style={{ marginTop: '8px' }}>
                                                                        <span style={{ color: '#888', fontSize: '12px'}}>{action.error}</span>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>

                                            {/* Hiển thị cho thiết bị di động */}
                                            <div className="d-md-none">
                                                {botActionData.map((action, index) => (
                                                    <Card
                                                        key={index}
                                                        className="mb-3"
                                                        style={{
                                                            borderRadius: '15px',
                                                            boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0)',
                                                            border: '1px solid #51cbce',
                                                            backgroundColor: getActionStatusBgColor(action.status)
                                                        }}
                                                    >
                                                        <CardHeader style={{ backgroundColor: '#f9f9f9', padding: '10px 15px', borderTopLeftRadius: '15px', borderTopRightRadius: '15px', borderBottom: '1px solid #51cbce' }}>
                                                            <strong style={{ color: '#51cbce' }}>{action.action}</strong>
                                                        </CardHeader>
                                                        <CardBody style={{ padding: '10px 15px' }}>
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span style={{ color: '#888' }}>Phần mềm:</span>
                                                                <span>{action.toolName}</span>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span style={{ color: '#888' }}>Thời gian:</span>
                                                                <span>{moment(action.createdAt).format('HH:mm:ss DD/MM/YYYY')}</span>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span style={{ color: '#888' }}>Người thực hiện:</span>
                                                                <span>{action.fullName}</span>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span style={{ color: '#888' }}>Trạng thái:</span>
                                                                <span style={{ color: getActionStatusColor(action.status) }}>{action.status}</span>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-2" style={{ marginTop: '10px' }}>
                                                                <span>{action.message}</span>
                                                            </div>
                                                            {action.error && (
                                                                <div style={{ marginTop: '8px', color: '#888', fontSize: '12px' }}>
                                                                    {action.error}
                                                                </div>
                                                            )}
                                                        </CardBody>
                                                    </Card>
                                                ))}
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        )
                    }
                </>
            )}
        </div>
    );
}

export default OrderIDDetails; 